/** @format */

import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

// Using global axios to inherit auth headers from axiosInterceptor for mobile browser support

// ==================== Types ====================

export interface FeeRevenueQueryParams {
	filter?: "all" | "past" | "today" | "future";
	startDate?: string; // ISO date string
	endDate?: string; // ISO date string
}

export interface StripeActivityQueryParams {
	filter?: "all" | "past" | "today" | "future";
	startDate?: string;
	endDate?: string;
}

// ==================== Platform Fee Revenue Service Methods ====================

/**
 * Get complete platform fee revenue report (late fees + payout fees)
 * with aggregations by month, circle, and member
 */
export const fetchPlatformFeeRevenue = async (
	params?: FeeRevenueQueryParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.filter) queryParams.append("filter", params.filter);
		if (params?.startDate) queryParams.append("startDate", params.startDate);
		if (params?.endDate) queryParams.append("endDate", params.endDate);

		const queryString = queryParams.toString();
		const url = `/admin/revenue/fees${queryString ? `?${queryString}` : ""}`;

		const response = await axios.get(`${API_BASE_URL}${url}`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get platform fee revenue error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get current Stripe account balance (available, pending, total)
 */
export const fetchStripeAccountBalance = async () => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/revenue/stripe/balance`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get Stripe balance error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get Stripe activity summary (charges, transfers) with optional date filtering
 */
export const fetchStripeActivitySummary = async (
	params?: StripeActivityQueryParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.filter) queryParams.append("filter", params.filter);
		if (params?.startDate) queryParams.append("startDate", params.startDate);
		if (params?.endDate) queryParams.append("endDate", params.endDate);

		const queryString = queryParams.toString();
		const url = `/admin/revenue/stripe/activity${queryString ? `?${queryString}` : ""}`;

		const response = await axios.get(`${API_BASE_URL}${url}`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get Stripe activity error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};
